import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import { useStaticQuery, graphql, Link } from 'gatsby';
import KeywordSearch from 'components/ProductCollectionPage/ProductFilters/KeywordSearch';
import slugify from 'utils/slugify';
import { SearchResultArrowIcon } from 'assets/svgs';

const quickSearches = [
  {
    title: 'Oak Staircases',
    path: '/stairs/oak/'
  },
  {
    title: 'Bannisters',
    path: '/stair-and-door-parts/?collection=bannisters'
  },
  {
    title: 'All Doors',
    path: '/doors/'
  }
];

function SearchDropdownMenu({ windowWidth, setShowSearchDropdownMenu }) {
  const [dataset, setDataset] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const staticQueryResults = useStaticQuery(graphql`
    query {
      stairs: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/local-data/stairs/collections/" } }
      ) {
        nodes {
          frontmatter {
            title
            slug
          }
        }
      }
      doors: allSanityDoor(filter: { images: { elemMatch: { asset: { _id: { ne: null } } } } }) {
        nodes {
          title
          productCollection
          productVariants {
            default {
              productId
            }
          }
        }
      }
      doorParts: allSanityDoorPart(
        filter: { images: { elemMatch: { asset: { _id: { ne: null } } } } }
      ) {
        nodes {
          title
          productCollection
          productVariants {
            default {
              productId
            }
          }
        }
      }
      stairParts: allSanityStairPart(
        filter: { images: { elemMatch: { asset: { _id: { ne: null } } } } }
      ) {
        nodes {
          title
          productCollection
          productVariants {
            default {
              productId
            }
          }
        }
      }
      showcases: allSanityShowcase {
        nodes {
          title
        }
      }
    }
  `);

  useEffect(() => {
    const _entries = [];
    const { stairs, doors, doorParts, stairParts, showcases } = staticQueryResults;
    if (stairs.nodes.length) {
      stairs.nodes.forEach((stairType) => {
        const slug = slugify(stairType.frontmatter.slug, {
          remove: /[*+~.,()'"!:@/]/g,
          lower: true,
          trim: true
        });

        const path = `/stairs/${slug}`;

        const entry = {
          title: stairType.frontmatter.title,
          path
        };

        _entries.push(entry);
      });
    }

    if (doors.nodes.length) {
      doors.nodes.forEach((door) => {
        const slug = `${slugify(door.title, {
          remove: /[*+~.,()'"!:@/]/g,
          lower: true,
          trim: true
        })}-${door.productVariants.default.productId.toLowerCase()}`;

        const path = `/doors/${slug}`;

        const entry = {
          title: door.title,
          path
        };

        _entries.push(entry);
      });

      const doorCollectionNames = doors.nodes.reduce((acc, node) => {
        const mergedArr = [...new Set([...acc, ...node.productCollection])];
        return mergedArr;
      }, []);

      if (doorCollectionNames.length) {
        doorCollectionNames.forEach((doorCollectionName) => {
          const slug = slugify(doorCollectionName, {
            remove: /[*+~.,()'"!:@/]/g,
            lower: true,
            trim: true
          });

          const path = `/doors/${slug}`;

          const entry = {
            title: doorCollectionName,
            path
          };

          _entries.push(entry);
        });
      }
    }

    if (doorParts.nodes.length) {
      doorParts.nodes.forEach((doorPart) => {
        const slug = `${slugify(doorPart.title, {
          remove: /[*+~.,()'"!:@/]/g,
          lower: true,
          trim: true
        })}-${doorPart.productVariants.default.productId.toLowerCase()}`;

        const path = `/stair-and-door-parts/${slug}`;

        const entry = {
          title: doorPart.title,
          path
        };

        _entries.push(entry);
      });
    }
    if (stairParts.nodes.length) {
      stairParts.nodes.forEach((stairPart) => {
        const slug = `${slugify(stairPart.title, {
          remove: /[*+~.,()'"!:@/]/g,
          lower: true,
          trim: true
        })}-${stairPart.productVariants.default.productId.toLowerCase()}`;

        const path = `/stair-and-door-parts/${slug}`;

        const entry = {
          title: stairPart.title,
          path
        };

        _entries.push(entry);
      });
    }
    if (showcases.nodes.length) {
      showcases.nodes.forEach((showcase) => {
        const slug = `${slugify(showcase.title, {
          remove: /[*+~.,()'"!:@/]/g,
          lower: true,
          trim: true
        })}`;

        const path = `/showcases/${slug}`;

        const entry = {
          title: showcase.title,
          path
        };

        _entries.push(entry);
      });
    }
    setDataset(_entries);
  }, [staticQueryResults]);

  useEffect(() => {
    if (dataset?.length) {
      if (searchQuery !== '') {
        const _searchResults = dataset.filter((entry) =>
          entry.title.toLowerCase().includes(searchQuery.toLowerCase())
        );
        if (_searchResults.length) {
          setSearchResults(_searchResults);
        }
      } else {
        setSearchResults([]);
      }
    }
  }, [searchQuery, dataset]);

  const handleSearchInput = ({ target }) => {
    const { value } = target;
    setSearchQuery(value);
  };

  return (
    <Container custom={windowWidth} onMouseLeave={() => setShowSearchDropdownMenu(false)}>
      <AnimatePresence>
        <InnerWrapper>
          <KeywordSearch
            searchQuery={searchQuery}
            handleSearchInput={handleSearchInput}
            placeholder="Start Search"
            autoFocus
          />
          <BottomTab>
            <p>{searchResults.length ? 'Results' : 'Quick Searches'}</p>
            <ul>
              {searchResults.length
                ? searchResults.map((result) => (
                    <Link
                      key={result.path}
                      to={result.path}
                      tabIndex={0}
                      onClick={() => setShowSearchDropdownMenu(false)}>
                      <li>
                        <SearchResultArrowIcon width="12px" />
                        {result.title}
                      </li>
                    </Link>
                  ))
                : quickSearches.map((result) => (
                    <Link
                      key={result.path}
                      to={result.path}
                      tabIndex={0}
                      onClick={() => setShowSearchDropdownMenu(false)}>
                      <li>
                        <SearchResultArrowIcon width="12px" />
                        {result.title}
                      </li>
                    </Link>
                  ))}
            </ul>
          </BottomTab>
        </InnerWrapper>
      </AnimatePresence>
    </Container>
  );
}

const Container = styled(motion.div).attrs({
  initial: {
    height: 0,
    opacity: 0,
    y: 0
  },
  animate: {
    height: 220,
    opacity: 1,
    y: '100%'
  },
  exit: {
    height: 220,
    opacity: 0,
    y: '100%'
  },
  transition: {
    type: 'tween',
    ease: 'easeInOut',
    duration: 0.5
  }
})`
  align-items: center;
  background-color: #f5f5f5;
  bottom: 0;
  overflow-y: hidden;
  position: absolute;
  width: 100vw;
  z-index: -1;
`;

const InnerWrapper = styled(motion.div).attrs({
  initial: {
    opacity: 0
  },
  animate: {
    opacity: 1,
    transition: {
      delay: 0.25
    }
  },
  exit: {
    opacity: 0,
    transition: {
      delay: 0
    }
  }
})`
  padding: 0 0.625rem;
  column-gap: 1.25rem;
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-template-rows: auto auto;
  grid-column: 1/-1;
  height: 100%;
  width: 100%;

  > div {
    grid-column: 1/-1;
  }

  /* KeywordSearch */
  > div:first-of-type {
    caret-color: var(--connolly-red);
    margin: 29px auto 0;
    max-width: 72%;
    width: 100%;
    grid-row: 1/2;

    @media (width >= 768px) {
      max-width: 660px;
    }

    svg {
      left: -1.75rem;
    }

    input {
      background-color: transparent;
      margin-bottom: 1rem;
      padding-left: 0.25rem;

      &::placeholder {
        color: var(--connolly-dark-grey);
        font-size: 1.25rem;
        font-weight: 700;
        text-transform: uppercase;
        padding-left: 0.25rem;
      }
    }
  }
`;

const BottomTab = styled.div`
  align-items: center;
  background-color: rgb(149 164 177 / 46%);
  border-radius: 6px 6px 0 0;
  display: flex;
  flex-direction: column;
  font-weight: 700;
  grid-row: 2/3;
  height: 140px;
  justify-content: center;
  max-width: 760px;
  padding: 15px 42px 8px;
  place-self: end center;
  text-align: left;
  text-transform: capitalize;
  width: 100%;

  ::-webkit-scrollbar {
    width: 0.5rem;
  }

  ::-webkit-scrollbar-track {
    border-radius: 0.625rem;
    box-shadow: inset 0 0 0.31rem var(--connolly-black);
  }

  ::-webkit-scrollbar-thumb {
    background-color: var(--connolly-red);
    border-radius: 0.625rem;
  }

  p {
    align-self: start;
    font-size: 0.875rem;
    margin-bottom: 0.55em;
  }

  ul {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    width: 100%;

    a {
      display: inline-block;
      position: relative;

      /* left: -1rem; */
      width: fit-content;

      &:not(:last-of-type) {
        margin-bottom: 0.7em;
      }
    }

    li {
      font-size: 1rem;

      /* line-height: 200%; */
    }
  }

  svg {
    margin-right: 12px;
  }

  a {
    text-decoration: none;
  }
`;

export default SearchDropdownMenu;
